import {
  Datagrid,
  ExportButton,
  FilterButton,
  List,
  TextField,
  TopToolbar,
  useListContext,
  FunctionField,
  useGetList,
  useUnselectAll,
  useUpdateMany,
  useNotify,
  useRefresh,
} from 'react-admin';
import { Typography, Stack, MenuItem, Select, Button, Switch } from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import { billerMappingFilters, useGetFilters } from './BillerMerchantCodeMappingFilters';
import { Check, Undo } from '@mui/icons-material';
import BillerMerchantCodeMappingAside from './BillerMerchantCodeMappingAside';

const UpdateMerchantCode = ({
  data,
  record,
  selected,
  setSelected,
}: {
  data: any;
  record: any;
  selected: string | undefined;
  setSelected: any;
}) => {
  const { selectedIds } = useListContext();

  const value =
    selected !== undefined && selectedIds.includes(record.id)
      ? selected
      : record.merchantCode;

  return (
    <Select
      size="small"
      sx={{
        minWidth: 200,
        p: 0,
        m: 0,
        fontSize: 14,
        '& .MuiSelect-select': {
          py: 0.15,
          backgroundColor:
            selected && selectedIds.includes(record.id)
              ? 'rgb(33 150 243 / 40%)'
              : 'transparent',
        },
      }}
      value={value}
      onChange={(d) => {
        setSelected(d.target.value);
      }}
      disabled={!selectedIds.includes(record.id)}
    >
      {data?.map((item: any) => (
        <MenuItem key={item.id} value={item.id}>
          {item.details || item.code}
        </MenuItem>
      ))}
    </Select>
  );
};

const ListActions = ({
  handleFilterWithUnmapped,
  filterWithUnmapped,
}: {
  handleFilterWithUnmapped: () => void;
  filterWithUnmapped: boolean;
}) => (
  <TopToolbar sx={{ alignItems: 'center' }}>
    <Button
      size="small"
      id="basic-button"
      onClick={handleFilterWithUnmapped}
    >
      {'Filter with unmapped'}
      <Switch
        sx={{ ml: 1 }}
        size="small"
        checked={filterWithUnmapped}
        onChange={handleFilterWithUnmapped}
      />
    </Button>

    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

const BillerMerchantCodeMappingListContent = () => {
  const [merchantCode, setMerchantCode] = useState<string | undefined>(undefined);

  const { data: billerMerchantCodes = [] } = useGetList('billerMerchantCodes', {
    pagination: { page: 1, perPage: 100 },
  });

  const merchantCodesForSelect = billerMerchantCodes.map((mc: any) => ({
    id: mc.merchantCode || mc.id,
    code: mc.merchantCode || mc.merchantId || mc.id,
    details: mc.details || mc.merchantId || mc.id,
  }));

  const { selectedIds } = useListContext();
  const { data } = useGetList('billerMerchantCodeMapping');

  useEffect(() => {
    const selectedRecord = data?.find((record) => record.id === selectedIds[0]);

    if (selectedIds?.length === 1) {
      if (selectedRecord) {
        setMerchantCode(selectedRecord.merchantCode || '');
      }
    } else if (merchantCode === undefined && selectedRecord) {
      setMerchantCode(selectedRecord.merchantCode || '');
    }
  }, [selectedIds, data]);

  const listContext = useListContext();
  const { filterValues, setFilters, displayedFilters, ...rest } = listContext;

  return (
    <Datagrid
      bulkActionButtons={
        <BulkActions selected={merchantCode} setSelected={setMerchantCode} />
      }
      rowClick="show"
      sx={{ opacity: 1 }}
    >
      <TextField label="Category ID" source="categoryId" />
      <TextField label="Category Name" source="categoryName" />
      <FunctionField
        label="Merchant Code"
        source="merchantCode"
        render={(record: any) => {
          return (
            <UpdateMerchantCode
              record={record}
              data={merchantCodesForSelect}
              selected={merchantCode}
              setSelected={setMerchantCode}
            />
          );
        }}
      />
    </Datagrid>
  );
};

const BulkActions = ({
  selected,
  setSelected,
}: {
  selected: string | undefined;
  setSelected: any;
}) => {
  const { selectedIds } = useListContext();
  const unselectAll = useUnselectAll('billerMerchantCodeMapping');
  const refresh = useRefresh();

  const [updateMany, { isLoading, error }] = useUpdateMany();

  const notify = useNotify();

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<Check />}
        size="small"
        disabled={selected === undefined || !selectedIds.length}
        onClick={async () => {
          try {
            await updateMany(
              'billerMerchantCodeMapping',
              {
                data: { merchantCode: selected, ids: selectedIds },
                ids: selectedIds,
              },
              {
                onSuccess: () => {
                  console.log(`success`);
                  setSelected('');
                  unselectAll();
                  refresh();
                  notify('Merchant code updated', { type: 'success' });
                },
                onError: (err) => {
                  console.log(`error`, err);
                },
                mutationMode: 'pessimistic',
              }
            );
          } catch (error) {
            notify('Error: Merchant code not updated', { type: 'error' });
          }
        }}
      >
        Update
      </Button>
      <Button
        startIcon={<Undo />}
        size="small"
        color="inherit"
        onClick={() => {
          setSelected('');
          unselectAll();
        }}
      >
        Undo
      </Button>
    </Stack>
  );
};

const BillerMerchantCodeMappingList = () => {
  const [filterWithUnmapped, setFilterWithUnmapped] = useState(false);
  const handleFilterWithUnmapped = () => {
    setFilterWithUnmapped(!filterWithUnmapped);
  };

  const filters = useGetFilters();

  return (
    <>
      <List
        sort={{ field: 'categoryId', order: 'ASC' }}
        perPage={25}
        filter={filterWithUnmapped ? { merchantCode: '' } : {}}
        filters={billerMappingFilters(filters)}
        actions={
          <ListActions
            handleFilterWithUnmapped={handleFilterWithUnmapped}
            filterWithUnmapped={filterWithUnmapped}
          />
        }
        aside={<BillerMerchantCodeMappingAside />}
        resource="billerMerchantCodeMapping"
      >
        <BillerMerchantCodeMappingListContent />
      </List>
    </>
  );
};

export default BillerMerchantCodeMappingList;
